// ─── src/filters/filterConfig.js ──────────────────────────────────────────────
// Category Filter Map, declares which filters each category uses.
// One config, all categories. New category? Add an entry or use _default.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Filter dimension definition:
 * @property {string} key       , unique filter key, maps to vendorFilters state
 * @property {string} label     , display label for the trigger button
 * @property {"select"|"range"|"boolean"} type, filter behaviour
 * @property {string} dataField , field on listing object. Suffix "[]" = array field
 * @property {boolean} [navigates], true = selecting this filter changes the URL (region only)
 * @property {string[]} [staticOptions], optional hardcoded options (overrides auto-population)
 */

// ── Sort options (shared across ALL categories) ──────────────────────────────
export const SORT_OPTIONS = [
  { slug: "recommended", name: "Recommended" },
  { slug: "rating",      name: "Highest Rated" },
  { slug: "price-low",   name: "Price: Low \u2192 High" },
  { slug: "price-high",  name: "Price: High \u2192 Low" },
  { slug: "reviews",     name: "Most Reviews" },
];

// ── Category Filter Map ─────────────────────────────────────────────────────

export const CATEGORY_FILTERS = {

  "wedding-venues": {
    filters: [
      { key: "region",     label: "Region",            type: "select", dataField: "regionSlug", navigates: true },
      { key: "venueType",  label: "Venue Type",        type: "select", dataField: "styles[]",
        staticOptions: ["Villa", "Luxury Hotel", "Castle", "Manor House", "Ch\u00e2teau", "Country Estate", "Boutique Estate", "Beach Resort", "Mountain Resort"] },
      { key: "priceRange", label: "Price Range",       type: "select", dataField: "priceFrom",
        staticOptions: ["Under \u00a310,000", "\u00a310,000\u2013\u00a325,000", "\u00a325,000\u2013\u00a350,000", "\u00a350,000\u2013\u00a3100,000", "\u00a3100,000+"] },
      { key: "guests",     label: "Number of Guests",  type: "select", dataField: "capacity",
        staticOptions: ["Up to 50 Guests", "50\u2013100 Guests", "100\u2013200 Guests", "200+ Guests"] },
    ],
    card: "LuxuryVenueCard",
  },

  "wedding-planners": {
    filters: [
      { key: "region",    label: "Region",        type: "select", dataField: "regionSlug", navigates: true },
      { key: "tier",      label: "Service Tier",   type: "select", dataField: "serviceTier", staticOptions: ["All", "Full Planning", "Partial Planning", "Day Coordination", "Design and Styling"] },
      { key: "specialty", label: "Specialty",      type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",         type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "photographers": {
    filters: [
      { key: "region",    label: "Region",  type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Style",   type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",  type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "videographers": {
    filters: [
      { key: "region",    label: "Region",  type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Style",   type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",  type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "florists": {
    filters: [
      { key: "region",    label: "Region",    type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Specialty", type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",    type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "caterers": {
    filters: [
      { key: "region",    label: "Region",    type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Specialty", type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",    type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "hair-makeup": {
    filters: [
      { key: "region",    label: "Region",    type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Specialty", type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",    type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "entertainment": {
    filters: [
      { key: "region",    label: "Region",    type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Type",      type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",    type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  // A category with NO entry here gets no tab on a destination page: the strip renders
  // `VENDOR_CATEGORIES.filter(vc => CATEGORY_FILTERS[vc.slug])`. Jewellers and bridal
  // boutiques were missing, so Sif Jakobs and 5 bridal boutiques appeared under "All"
  // with no way to filter to them. Any new vertical needs a config here, or it is
  // reachable only by scrolling All.
  "jewellers": {
    filters: [
      { key: "region",    label: "Region", type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Style",  type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget", type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "bridal-boutiques": {
    filters: [
      { key: "region",    label: "Region", type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Style",  type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget", type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "wedding-cakes": {
    filters: [
      { key: "region",    label: "Region",    type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Style",     type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",    type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  "wedding-vendors": {
    filters: [
      { key: "region",    label: "Region",      type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Vendor Type", type: "select", dataField: "category",
        // Wedding Planners always leads the list, ahead of the alphabetical rest.
        pinFirst: ["wedding-planners"],
        // Maps raw category slugs (stored in DB) to human-readable labels for display.
        // Filter matching still uses the slug value so extraction + matching stay consistent.
        optionLabels: {
          "photographers":    "Photographers",
          "florists":         "Florists",
          "caterers":         "Caterers",
          "wedding-planners": "Wedding Planners",
          "videographers":    "Videographers",
          "bridal-boutiques": "Bridal Boutiques",
          "entertainment":    "Entertainment",
          "hair-makeup":      "Hair & Makeup",
          "jewellers":        "Jewellers",
          "transport":        "Transport",
          "stationery":       "Stationery",
          "celebrants":       "Celebrants",
          "event-design":     "Event Design & Styling",
          "event-production": "Event Production",
          "wedding-cakes":    "Wedding Cakes",
          "accessories":      "Accessories",
          "health-beauty":    "Health & Beauty",
          "guest-attire":     "Guest Attire",
          "bridal-wear":      "Bridal Wear",
        },
      },
      { key: "budget",    label: "Budget",      type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },

  // ── Default: any category not explicitly listed above ──────────────────────
  "_default": {
    filters: [
      { key: "region",    label: "Region",    type: "select", dataField: "regionSlug", navigates: true },
      { key: "specialty", label: "Specialty", type: "select", dataField: "specialties[]" },
      { key: "budget",    label: "Budget",    type: "select", dataField: "priceLabel" },
    ],
    card: "LuxuryVendorCard",
  },
};

// ── Helper: resolve config for a category slug ──────────────────────────────
export function getFilterConfig(categorySlug) {
  return CATEGORY_FILTERS[categorySlug] || CATEGORY_FILTERS["_default"];
}

// ── Helper: build initial filter state from config ──────────────────────────
export function buildInitialFilters(config, regionName) {
  const state = { sort: "recommended" };
  config.filters.forEach(dim => {
    if (dim.key === "region") {
      state[dim.key] = regionName || "All";
    } else {
      state[dim.key] = "All";
    }
  });
  return state;
}

// ── Helper: check if any filter is active ───────────────────────────────────
export function hasActiveFilters(filters) {
  return Object.entries(filters).some(([key, val]) => {
    if (key === "sort") return val !== "recommended";
    return val && val !== "All";
  });
}

// ── Helper: reset all filters to defaults ───────────────────────────────────
export function resetFilters(config) {
  return buildInitialFilters(config, "All");
}
